import React, {useCallback, useEffect, useState} from 'react';
import Layout from '../components/Layout';
import EventModal from '../components/EventModal';
import {api} from '../../api';

const VIEWS = ['Day', 'Work Week', 'Month'];

function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function fmt(d) { return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); }
function fmtTime(iso) { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }

function getRange(view, date) {
  const d = startOfDay(date);
  if (view === 'Day') return { start: d, end: addDays(d, 1) };
  if (view === 'Work Week') {
    const day = d.getDay();
    const mon = addDays(d, -(day === 0 ? 6 : day - 1));
    return { start: mon, end: addDays(mon, 5) };
  }
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const startDay = first.getDay();
  const gridStart = addDays(first, -startDay);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const endDay = last.getDay();
  const gridEnd = addDays(last, 7 - endDay);
  return { start: gridStart, end: gridEnd };
}

function navigate(view, date, dir) {
  if (view === 'Day') return addDays(date, dir);
  if (view === 'Work Week') return addDays(date, dir * 7);
  return new Date(date.getFullYear(), date.getMonth() + dir, 1);
}

function getLabel(view, date) {
  if (view === 'Day') return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  if (view === 'Work Week') {
    const { start, end } = getRange(view, date);
    return `${fmt(start)} – ${fmt(addDays(end, -1))}`;
  }
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const COLOR_MAP = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-500',   text: 'text-blue-900',   hover: 'hover:bg-blue-100',   pill: 'bg-blue-100 text-blue-800' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-500',   text: 'text-teal-900',   hover: 'hover:bg-teal-100',   pill: 'bg-teal-100 text-teal-800' },
  red:    { bg: 'bg-red-50',    border: 'border-red-500',    text: 'text-red-900',    hover: 'hover:bg-red-100',    pill: 'bg-red-100 text-red-800' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-900', hover: 'hover:bg-orange-100', pill: 'bg-orange-100 text-orange-800' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-900', hover: 'hover:bg-yellow-100', pill: 'bg-yellow-100 text-yellow-800' },
  green:  { bg: 'bg-green-50',  border: 'border-green-500',  text: 'text-green-900',  hover: 'hover:bg-green-100',  pill: 'bg-green-100 text-green-800' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-900', hover: 'hover:bg-purple-100', pill: 'bg-purple-100 text-purple-800' },
  pink:   { bg: 'bg-pink-50',   border: 'border-pink-500',   text: 'text-pink-900',   hover: 'hover:bg-pink-100',   pill: 'bg-pink-100 text-pink-800' },
  gray:   { bg: 'bg-gray-50',   border: 'border-gray-500',   text: 'text-gray-900',   hover: 'hover:bg-gray-100',   pill: 'bg-gray-100 text-gray-800' },
};
function ec(e) { return COLOR_MAP[e.color] || COLOR_MAP.blue; }

function useCurrentTime() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);
  return now;
}

function DayColumn({ date, events, onSlotClick, onEventClick, isToday, now }) {
  const dayStart = startOfDay(date).getTime();
  const dayEvents = events.filter(e => {
    const s = new Date(e.startTime).getTime();
    const eEnd = new Date(e.endTime).getTime();
    return s < dayStart + 86400000 && eEnd > dayStart;
  });
  const allDayEvents = dayEvents.filter(e => e.allDay);
  const timedEvents = dayEvents.filter(e => !e.allDay);
  const nowTop = isToday ? (now.getHours() + now.getMinutes() / 60) * 48 : null;

  return (
    <div className={`flex-1 min-w-0 ${isToday ? 'bg-blue-50/40' : ''}`}>
      {allDayEvents.length > 0 && (
        <div className="border-b border-gray-200 px-1 py-1 space-y-1">
          {allDayEvents.map(e => {
            const c = ec(e);
            return (
              <div key={e.id} onClick={() => onEventClick(e)}
                className={`text-xs px-2 py-1 rounded truncate cursor-pointer ${c.pill} ${c.hover}`}>
                {e.title}
              </div>
            );
          })}
        </div>
      )}
      <div className="relative" style={{ height: 1152 }}>
        {HOURS.map(h => {
          const isBusiness = h >= 8 && h < 18;
          return (
            <div key={h} className={`absolute w-full ${isBusiness ? 'bg-white' : 'bg-gray-50'}`}
              style={{ top: h * 48, height: 48 }}
              onClick={() => { const d = new Date(date); d.setHours(h); onSlotClick(d); }}>
              <div className="absolute inset-x-0 top-0 h-px bg-gray-200" />
              <div className="absolute inset-x-0 top-1/2 h-px bg-gray-100" style={{ backgroundImage: 'repeating-linear-gradient(to right, transparent, transparent 4px, #e5e7eb 4px, #e5e7eb 8px)' }} />
            </div>
          );
        })}
        {nowTop !== null && (
          <div className="absolute w-full z-20" style={{ top: nowTop }}>
            <div className="h-px w-full bg-red-500" />
          </div>
        )}
        {timedEvents.map(e => {
          const s = new Date(e.startTime);
          const end = new Date(e.endTime);
          const top = (s.getHours() + s.getMinutes() / 60) * 48;
          const height = Math.max(((end - s) / 3600000) * 48, 20);
          return (
            <div key={e.id} onClick={() => onEventClick(e)}
              className={`absolute left-1 right-1 rounded-md px-2 py-1 text-xs cursor-pointer overflow-hidden border-l-4 ${ec(e).bg} ${ec(e).text} ${ec(e).border} ${ec(e).hover}`}
              style={{ top, height, zIndex: 10 }}>
              <p className="font-medium truncate">{e.title}</p>
              {height > 28 && <p className="truncate opacity-75">{fmtTime(e.startTime)} – {fmtTime(e.endTime)}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

function TimeGrid({ dates, events, onSlotClick, onEventClick, now }) {
  const today = startOfDay(new Date()).getTime();
  const colMin = dates.length > 1 ? 'min-w-[140px]' : '';
  return (
    <div className="bg-white rounded-lg shadow overflow-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
      <div className="flex border-b border-gray-300 sticky top-0 bg-white z-10">
        <div className="w-12 md:w-16 shrink-0 border-r border-gray-200" />
        {dates.map(d => {
          const isT = startOfDay(d).getTime() === today;
          return (
            <div key={d.toISOString()} className={`flex-1 ${colMin} text-center py-2 text-sm font-medium border-l border-gray-200 ${isT ? 'bg-blue-50' : ''}`}>
              <span className={isT ? 'text-blue-600' : 'text-gray-500'}>{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
              {' '}
              <span className={isT ? 'bg-blue-600 text-white px-1.5 py-0.5 rounded-full' : 'text-gray-900'}>{d.getDate()}</span>
            </div>
          );
        })}
      </div>
      <div className="flex">
        <div className="w-12 md:w-16 shrink-0 border-r border-gray-200">
          {HOURS.map(h => (
            <div key={h} className="h-12 text-right pr-1 md:pr-2 text-xs text-gray-400 leading-none pt-0.5">
              {h === 0 ? '' : `${h % 12 || 12}${h < 12 ? 'a' : 'p'}`}
            </div>
          ))}
        </div>
        {dates.map(d => (
          <div key={d.toISOString()} className={`flex-1 ${colMin} border-l border-gray-200`}>
            <DayColumn date={d} events={events} onSlotClick={onSlotClick} onEventClick={onEventClick} isToday={startOfDay(d).getTime() === today} now={now} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthGrid({ date, events, onSlotClick, onEventClick }) {
  const { start, end } = getRange('Month', date);
  const weeks = [];
  let d = new Date(start);
  while (d < end) {
    const week = [];
    for (let i = 0; i < 7; i++) { week.push(new Date(d)); d = addDays(d, 1); }
    weeks.push(week);
  }
  const today = startOfDay(new Date()).getTime();
  const currentMonth = date.getMonth();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="grid grid-cols-7 border-b border-gray-300">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
          <div key={d} className={`text-center py-2 text-xs font-medium uppercase ${i === 0 || i === 6 ? 'text-gray-400 bg-amber-50/50' : 'text-gray-500'}`}>{d}</div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b last:border-b-0" style={{ minHeight: 100 }}>
          {week.map((day, di) => {
            const dayStart = startOfDay(day).getTime();
            const dayEvents = events.filter(e => {
              const s = new Date(e.startTime).getTime();
              const eEnd = new Date(e.endTime).getTime();
              return s < dayStart + 86400000 && eEnd > dayStart;
            });
            const isToday = dayStart === today;
            const isCurrentMonth = day.getMonth() === currentMonth;
            const isWeekend = di === 0 || di === 6;
            let bg = '';
            if (isToday) bg = 'bg-blue-50/50';
            else if (!isCurrentMonth) bg = 'bg-gray-100/60';
            else if (isWeekend) bg = 'bg-amber-50/30';
            return (
              <div key={day.toISOString()} className={`border-r last:border-r-0 p-1 cursor-pointer hover:bg-blue-50/30 transition-colors ${bg}`}
                onClick={() => onSlotClick(day)}>
                <div className={`text-sm mb-1 ${isToday ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                  {day.getDate()}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(e => (
                    <div key={e.id} onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                      className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer border-l-2 ${ec(e).bg} ${ec(e).text} ${ec(e).border} ${ec(e).hover}`}>
                      {!e.allDay && <span className="font-medium">{fmtTime(e.startTime)} </span>}{e.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="text-xs text-gray-500 px-1">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function Calendar() {
  const isMobile = useIsMobile();
  const [view, setView] = useState(isMobile ? 'Day' : 'Work Week');
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [modal, setModal] = useState(null);
  const now = useCurrentTime();

  useEffect(() => { if (isMobile && view === 'Work Week') setView('Day'); }, [isMobile, view]);

  const loadEvents = useCallback(() => {
    const { start, end } = getRange(view, date);
    const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
    api(`/calendar/events?${params}`).then(r => r.ok ? r.json() : []).then(setEvents);
  }, [view, date]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const handleSlotClick = (d) => {
    const s = new Date(d);
    const e = new Date(d);
    if (view === 'Month') {
      s.setHours(9, 0); e.setHours(10, 0);
    } else {
      e.setHours(s.getHours() + 1);
    }
    const toLocal = (dt) => new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setModal({ defaultStart: toLocal(s), defaultEnd: toLocal(e), defaultAllDay: false });
  };

  const handleEventClick = (event) => setModal({ event });

  const getDates = () => {
    const { start, end } = getRange(view, date);
    const dates = [];
    let d = new Date(start);
    while (d < end) { dates.push(new Date(d)); d = addDays(d, 1); }
    return dates;
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setDate(d => navigate(view, d, -1))} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">&lsaquo;</button>
          <button onClick={() => setDate(new Date())} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Today</button>
          <button onClick={() => setDate(d => navigate(view, d, 1))} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">&rsaquo;</button>
          <h2 className="text-lg font-semibold text-gray-900 ml-2">{getLabel(view, date)}</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            {VIEWS.map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-2 text-sm ${v === view ? 'bg-blue-600 text-white' : 'hover:bg-gray-50'}`}>{v}</button>
            ))}
          </div>
          <button onClick={() => setModal({ defaultStart: '', defaultEnd: '', defaultAllDay: false })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">+ Event</button>
        </div>
      </div>

      {view === 'Month' ? (
        <MonthGrid date={date} events={events} onSlotClick={handleSlotClick} onEventClick={handleEventClick} />
      ) : (
        <TimeGrid dates={getDates()} events={events} onSlotClick={handleSlotClick} onEventClick={handleEventClick} now={now} />
      )}

      {modal && (
        <EventModal
          event={modal.event}
          defaultStart={modal.defaultStart}
          defaultEnd={modal.defaultEnd}
          defaultAllDay={modal.defaultAllDay}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadEvents(); }}
          onDeleted={() => { setModal(null); loadEvents(); }}
        />
      )}
    </Layout>
  );
}
